import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import {
	type TopLevelComment,
	type CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import { ContentAnalyticsViewedComments } from '@confluence/inline-comments-queries/entry-points/ContentAnalyticsViewedComments.graphql';

import type {
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment as UnreadCommentNode,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_replies_Comment as UnreadCommentReply,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment_location_InlineComment as UnreadLocationInlineComment,
} from '../../__types__/UnreadCommentCountUnreadCommentQuery';
import type { ThreadKeyToCommentIdMap, UnreadComment } from '../../types/unreadCommentsTypes';

// Putting this here to not have a circular dependency
export enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

export interface CreateUnreadCommentListProps {
	allComments: (UnreadCommentNode | null)[] | null | undefined;
	updateUnreadCommentsListState: (
		fn: (unreadCommentsListState: UnreadComment[]) => UnreadComment[],
	) => void;
	userId: string | null;
	pageId?: string;
	updateReadCommentsListState: (fn: (readComments: Set<string>) => Set<string>) => void;
	updateThreadKeyToCommentIdMap: (
		fn: (threadKeyToCommentIdMap: ThreadKeyToCommentIdMap) => ThreadKeyToCommentIdMap,
	) => void;
	threadKeyToCommentIdMap: ThreadKeyToCommentIdMap;
	resetStates: () => void;
	editorApi?: ExtractPresetAPI<any>;
	pageMode?: 'view' | 'edit';
	annotationsInEditorDoc: Set<string>;
}

type ValidUnreadComment = {
	comment: UnreadCommentNode | UnreadCommentReply | TopLevelComment | CommentReply;
	viewedComments: Set<string>;
	userId: string;
	annotationsInEditorDoc?: Set<string>;
	pageMode?: 'view' | 'edit';
};

export const isValidUnreadComment = ({
	comment,
	viewedComments,
	userId,
	annotationsInEditorDoc,
	pageMode = 'view',
}: ValidUnreadComment) => {
	const isUnread = !viewedComments.has(comment.id);
	const commentLocation = comment?.location as UnreadLocationInlineComment;
	const isUnresolved = !commentLocation?.inlineResolveProperties?.resolved;
	const isNotOwnComment = 'accountId' in comment.author && comment.author?.accountId !== userId;
	const createdLast30Days =
		new Date(comment.createdAt?.value) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
	const markerRefExists = commentLocation?.inlineMarkerRef;
	const isAnnotationInDoc =
		pageMode === 'edit' ? markerRefExists && annotationsInEditorDoc?.has(markerRefExists) : true;
	const isGeneralComment = comment?.location.type === 'FOOTER';

	return (
		isUnread &&
		isUnresolved &&
		isNotOwnComment &&
		createdLast30Days &&
		(isGeneralComment || (markerRefExists && isAnnotationInDoc))
	);
};

export const createUnreadCommentList = async ({
	allComments,
	updateUnreadCommentsListState,
	userId,
	pageId,
	updateReadCommentsListState,
	updateThreadKeyToCommentIdMap,
	resetStates,
	pageMode = 'view',
	annotationsInEditorDoc,
}: CreateUnreadCommentListProps) => {
	const hasUnresolvedComments =
		allComments &&
		allComments.some(
			(comment) =>
				!(comment?.location as UnreadLocationInlineComment)?.inlineResolveProperties?.resolved,
		);

	if (hasUnresolvedComments && pageId) {
		try {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const data = await getApolloClient().query({
				query: ContentAnalyticsViewedComments,
				variables: {
					contentId: pageId,
				},
				fetchPolicy: 'network-only',
			});

			const viewedComments: Set<string> = new Set(
				data?.data?.contentAnalyticsViewedComments?.commentIds,
			);
			updateReadCommentsListState(() => viewedComments);

			const unreadComments: UnreadComment[] = [];
			const threadKeyMap: ThreadKeyToCommentIdMap = {};
			allComments.forEach((comment: UnreadCommentNode | null) => {
				if (!comment) {
					return;
				}

				const commentType =
					comment.location.type === 'INLINE' ? CommentType.INLINE : CommentType.GENERAL;
				const threadKey =
					commentType === CommentType.INLINE
						? (comment.location as UnreadLocationInlineComment)?.inlineMarkerRef
						: comment.id;

				if (userId && threadKey) {
					if (
						isValidUnreadComment({
							comment,
							viewedComments,
							userId,
							annotationsInEditorDoc,
							pageMode,
						})
					) {
						unreadComments.push({
							id: comment.id,
							threadKey: threadKey ?? '',
							commentType,
						});
					}

					if (comment.replies) {
						comment.replies.forEach((reply) => {
							if (reply) {
								if (
									isValidUnreadComment({
										comment: reply,
										viewedComments,
										userId,
										annotationsInEditorDoc,
										pageMode,
									})
								) {
									unreadComments.push({
										id: reply.id,
										threadKey: threadKey ?? '',
										commentType,
									});
								}
								if (!threadKeyMap[threadKey]) {
									threadKeyMap[threadKey] = new Set([reply.id]);
								} else if (threadKeyMap[threadKey]) {
									threadKeyMap[threadKey].add(reply.id);
								}
							}
						});
					}
				}
			});

			updateThreadKeyToCommentIdMap((prevState) => {
				const mergedThreadKeyMap = { ...prevState };
				Object.keys(threadKeyMap).forEach((key) => {
					if (!mergedThreadKeyMap[key]) {
						mergedThreadKeyMap[key] = threadKeyMap[key];
					} else {
						mergedThreadKeyMap[key] = new Set([...mergedThreadKeyMap[key], ...threadKeyMap[key]]);
					}
				});

				return mergedThreadKeyMap;
			});

			updateUnreadCommentsListState(() => unreadComments);
			// Once we are done updating the unread list, need to reset the states.
			resetStates();
		} catch {
			// We don't want to show any unread count in the comment button if there's an error
			resetStates();
		}
	}
};
